import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Modal as AntModal } from 'antd';
import type { ModalProps as AntModalProps } from 'antd/lib/modal';
import { Button, ButtonGroup } from '@common/components';
import { createRoot } from 'react-dom/client';

type ModalContextType = {
  closeModal: () => void;
};

const ModalContext = createContext<ModalContextType>({
  closeModal: () => {},
});

export const useModalContext = () => useContext(ModalContext);

type ModalProps = {
  cancelText?: string;
  okText?: string;
  onOk?: () => void;
  onCancel?: () => void;
  afterClose?: () => void;
  visible?: boolean;
  trigger?: ReactNode;
  danger?: boolean;
  hideCancelButton?: boolean;
  preventClose?: boolean;
} & Omit<AntModalProps, 'closable'>;

export const Modal = ({
  cancelText = 'Cancel',
  okText = 'Confirm',
  onOk = () => {},
  onCancel = () => {},
  afterClose = () => {},
  children,
  trigger,
  confirmLoading,
  className,
  visible = false,
  footer,
  danger,
  hideCancelButton,
  preventClose = false,
  ...other
}: ModalProps) => {
  const [showModal, setShowModal] = useState(visible);
  const isConfirmationLoadingDefined = confirmLoading !== undefined;

  useEffect(() => {
    setShowModal(visible);
  }, [visible]);

  const effectiveFooter = footer ? (
    footer
  ) : (
    <ButtonGroup align="right">
      {!hideCancelButton && (
        <Button
          size="md"
          dataTestId="cancel-button"
          onClick={() => {
            onCancel();
            setShowModal(false);
          }}
        >
          {cancelText}
        </Button>
      )}
      <Button
        kind="primary"
        size="md"
        dataTestId="ok-button"
        danger={danger}
        loading={confirmLoading}
        onClick={() => {
          onOk();

          if (!isConfirmationLoadingDefined) {
            setShowModal(false);
          }
        }}
      >
        {okText}
      </Button>
    </ButtonGroup>
  );

  return (
    <>
      <ModalContext.Provider value={{ closeModal: () => setShowModal(false) }}>
        <AntModal
          className={`wk-modal ${className || ''}`}
          onCancel={() => {
            if (preventClose) return;
            onCancel?.();
            setShowModal(false);
          }}
          open={showModal}
          afterClose={afterClose}
          footer={effectiveFooter}
          {...other}
          closable={!preventClose}
        >
          {children}
        </AntModal>
      </ModalContext.Provider>
      {trigger && <a onClick={() => setShowModal(true)}>{trigger}</a>}
      <style jsx>{`
        :global(.wk-modal .ant-modal-footer:empty) {
          display: none;
        }
      `}</style>
    </>
  );
};

Modal.confirm = ({
  content,
  danger,
  ...props
}: Omit<ModalProps, 'children' | 'visible'> & {
  content: ReactNode;
  danger?: boolean;
}) => {
  const div = document.createElement('div');
  div.className = 'wk-modal-confirm';
  document.body.appendChild(div);

  const root = createRoot(div);

  function cleanUpDOM() {
    setTimeout(() => {
      root.unmount();

      document
        .querySelectorAll('.wk-modal-confirm')
        .forEach((el) => el.remove());
    }, 200); // wait animation end
  }

  function onOk() {
    props?.onOk?.();
    cleanUpDOM();
  }

  function onCancel() {
    props?.onCancel?.();
    cleanUpDOM();
  }

  root.render(
    <Modal {...props} visible onOk={onOk} onCancel={onCancel} danger={danger}>
      {content}
    </Modal>
  );
};
